import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'

import LineCropIcon from 'LuxKit/Icons/line/LineCropIcon'
import focusStyle from 'styles/utils/focusStyle'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'

import {
  PROPERTY_MAP_CONTROL_BUTTON_SIZE,
  PROPERTY_MAP_CONTROL_ICON_SIZE,
  PROPERTY_MAP_STATIC_IN_VIEW_ROOT_MARGIN,
} from '../constants'
import StaticPropertyMap from '../StaticPropertyMap'
import FullScreenMapModal from './FullScreenMapModal'
import type { StaticPropertyMapWithInteractiveModalProps } from './types'

const StaticMapButton = styled.button`
  position: relative;
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  border-radius: ${props => props.theme.borderRadius.S};
  background: transparent;
  cursor: pointer;

  &:disabled {
    cursor: wait;
  }

  &:focus-visible {
    ${focusStyle}
  }
`

const FullscreenButtonOverlay = styled.div`
  position: absolute;
  top: ${rem(8)};
  inset-inline-end: ${rem(8)};
  display: flex;
  width: ${rem(PROPERTY_MAP_CONTROL_BUTTON_SIZE)};
  height: ${rem(PROPERTY_MAP_CONTROL_BUTTON_SIZE)};
  border-radius: ${props => props.theme.borderRadius.S};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  box-shadow: ${props => props.theme.shadow.flat.small};
  pointer-events: none;
`

const FullscreenButtonOverlayIcon = styled(LineCropIcon)`
  margin: auto;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
`

const LoadingOverlay = styled(LoadingIndicator)`
  border-radius: ${props => props.theme.borderRadius.S};
`

function StaticPropertyMapWithInteractiveModal(props: StaticPropertyMapWithInteractiveModalProps) {
  const {
    className,
    disablePanOnMarkerSelect,
    isSnapBackToOfferEnabled,
    focusOnFirstMarker,
    height,
    highlightedMarkerId,
    interactiveMarkers,
    isLoading = false,
    markerOptions,
    markers,
    mapboxAccessTokenUseCase,
    mapLoadingElement,
    mapOptions,
    modalTitle,
    multipleMarkers,
    onDrag,
    onScrolledIntoView,
    onSelectMarker,
    renderInfoWindow,
    showActiveMarkerState,
    wrapperStyle,
  } = props
  const intl = useIntl()
  const hasFiredScrolledIntoViewRef = useRef(false)
  const [isMapOpen, setIsMapOpen] = useState(false)
  const [isOpenRequested, setIsOpenRequested] = useState(false)
  const [inViewRef, hasScrolledIntoView] = useInView({
    rootMargin: PROPERTY_MAP_STATIC_IN_VIEW_ROOT_MARGIN,
    triggerOnce: true,
  })
  const modalMarkers = interactiveMarkers ?? markers
  const isSkeleton = !hasScrolledIntoView
  const showLoadingOverlay = isOpenRequested && isLoading && !isSkeleton

  const openMapLabel = intl.formatMessage({
    id: 'MY6qvf8O8nwMyrM8PJ_Z',
    defaultMessage: 'Open interactive map',
  })

  useEffect(() => {
    if (!hasScrolledIntoView || !onScrolledIntoView || hasFiredScrolledIntoViewRef.current) {
      return
    }

    hasFiredScrolledIntoViewRef.current = true
    onScrolledIntoView()
  }, [hasScrolledIntoView, onScrolledIntoView])

  const closeMap = useCallback(() => {
    setIsMapOpen(false)
  }, [])

  const openMap = useCallback(() => {
    setIsMapOpen(true)
  }, [])

  const fullscreenMapModal = useMemo(() => {
    if (!isMapOpen) {
      return undefined
    }

    return (
      <FullScreenMapModal
        className={className}
        disablePanOnMarkerSelect={disablePanOnMarkerSelect}
        isSnapBackToOfferEnabled={isSnapBackToOfferEnabled}
        focusOnFirstMarker={focusOnFirstMarker}
        highlightedMarkerId={highlightedMarkerId}
        isOpen
        markerOptions={markerOptions}
        markers={modalMarkers}
        mapboxAccessTokenUseCase={mapboxAccessTokenUseCase}
        mapLoadingElement={mapLoadingElement}
        mapOptions={mapOptions}
        modalTitle={modalTitle}
        multipleMarkers={multipleMarkers}
        showActiveMarkerState={showActiveMarkerState}
        onClose={closeMap}
        onDrag={onDrag}
        onSelectMarker={onSelectMarker}
        renderInfoWindow={renderInfoWindow}
      />
    )
  }, [
    className,
    closeMap,
    disablePanOnMarkerSelect,
    isMapOpen,
    isSnapBackToOfferEnabled,
    focusOnFirstMarker,
    highlightedMarkerId,
    markerOptions,
    mapboxAccessTokenUseCase,
    mapLoadingElement,
    mapOptions,
    modalMarkers,
    modalTitle,
    multipleMarkers,
    onDrag,
    onSelectMarker,
    renderInfoWindow,
    showActiveMarkerState,
  ])

  useEffect(() => {
    if (!isOpenRequested || isLoading || isSkeleton) {
      return
    }

    setIsOpenRequested(false)
    openMap()
  }, [isLoading, isOpenRequested, isSkeleton, openMap])

  const handleOpenMap = useCallback(() => {
    if (isSkeleton || isOpenRequested) {
      return
    }

    if (isLoading) {
      setIsOpenRequested(true)
      return
    }

    openMap()
  }, [isLoading, isOpenRequested, isSkeleton, openMap])

  const fullscreenButtonOverlay = useMemo(() => {
    if (isSkeleton || showLoadingOverlay || markers.length === 0) {
      return undefined
    }

    return (
      <FullscreenButtonOverlay
        aria-hidden="true"
        data-testid="static-property-map-fullscreen-overlay"
      >
        <FullscreenButtonOverlayIcon size={PROPERTY_MAP_CONTROL_ICON_SIZE} />
      </FullscreenButtonOverlay>
    )
  }, [isSkeleton, markers.length, showLoadingOverlay])

  return (
    <StaticMapButton
      ref={inViewRef}
      type="button"
      aria-label={openMapLabel}
      disabled={isSkeleton || showLoadingOverlay}
      onClick={handleOpenMap}
    >
      <StaticPropertyMap
        className={className}
        focusOnFirstMarker={focusOnFirstMarker}
        height={height}
        highlightedMarkerId={highlightedMarkerId}
        isLoading={isSkeleton}
        markers={markers}
        mapboxAccessTokenUseCase={mapboxAccessTokenUseCase}
        wrapperStyle={wrapperStyle}
      />
      {showLoadingOverlay && (
        <LoadingOverlay
          data-testid="static-property-map-loading-overlay"
          floating
          backdrop="white"
        />
      )}
      {fullscreenButtonOverlay}
      {fullscreenMapModal}
    </StaticMapButton>
  )
}

export default React.memo(StaticPropertyMapWithInteractiveModal)
